import type { FC } from "react";
import "./SovTrend.css";

type Series = {
	label: string;
	values: Array<number | null>;
};

type Props = {
	months: string[];
	series: Series[];
};

const WIDTH = 640;
const HEIGHT = 220;
const MARGIN = { top: 12, right: 16, bottom: 28, left: 36 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const GRIDLINES = [0, 25, 50, 75, 100];
const SERIES_COLORS = ["var(--bs-primary)", "var(--bs-secondary)"];

const numericStyle = { fontVariantNumeric: "tabular-nums" } as const;

const xFor = (index: number, count: number) =>
	MARGIN.left + (count <= 1 ? 0 : (index / (count - 1)) * PLOT_WIDTH);

const yFor = (value: number) =>
	MARGIN.top + (1 - Math.min(100, Math.max(0, value)) / 100) * PLOT_HEIGHT;

/** Builds an SVG path, breaking the line wherever a value is null. */
const pathFor = (values: Array<number | null>) => {
	let d = "";
	let drawing = false;

	values.forEach((value, index) => {
		if (value === null) {
			drawing = false;
			return;
		}

		const x = xFor(index, values.length);
		const y = yFor(value);
		d += `${drawing ? "L" : "M"}${x},${y} `;
		drawing = true;
	});

	return d.trim();
};

export const SovTrend: FC<Readonly<Props>> = ({ months, series }) => {
	return (
		<div className="sov-trend">
			<div className="sov-trend-legend">
				{series.map((s, index) => (
					<span key={s.label} className="sov-trend-legend-item">
						<span
							className="sov-trend-swatch"
							style={{
								backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
							}}
						/>
						{s.label}
					</span>
				))}
			</div>
			<svg
				viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
				role="img"
				aria-label="Share of voice trend over time"
				className="sov-trend-svg"
			>
				{GRIDLINES.map((pct) => (
					<g key={pct}>
						<line
							x1={MARGIN.left}
							x2={WIDTH - MARGIN.right}
							y1={yFor(pct)}
							y2={yFor(pct)}
							className="sov-trend-gridline"
						/>
						<text
							x={MARGIN.left - 6}
							y={yFor(pct)}
							textAnchor="end"
							dominantBaseline="middle"
							className="sov-trend-axis-label"
							style={numericStyle}
						>
							{pct}%
						</text>
					</g>
				))}

				{months.map((month, index) => (
					<text
						key={month}
						x={xFor(index, months.length)}
						y={HEIGHT - 6}
						textAnchor="middle"
						className="sov-trend-axis-label"
					>
						{month}
					</text>
				))}

				{series.map((s, seriesIndex) => (
					<g key={s.label}>
						<path
							d={pathFor(s.values)}
							fill="none"
							stroke={SERIES_COLORS[seriesIndex % SERIES_COLORS.length]}
							strokeWidth={2}
						/>
						{s.values.map((value, index) =>
							value === null ? null : (
								<circle
									key={months[index]}
									cx={xFor(index, s.values.length)}
									cy={yFor(value)}
									r={3}
									fill={SERIES_COLORS[seriesIndex % SERIES_COLORS.length]}
								>
									<title>
										{s.label} · {months[index]}: {Math.round(value)}%
									</title>
								</circle>
							),
						)}
					</g>
				))}
			</svg>
		</div>
	);
};
